import React from "react";
import { IMAGES, REGULATORS, FOUNDER, BRAND, LIFE_CYCLE_CTA } from "@/lib/content";
import { MapPin, Calendar, Award, ArrowRight } from "lucide-react";

const AboutUs = () => {
  return (
    <section
      id="nosotros"
      data-testid="about-section"
      className="relative bg-[#0B0410] text-[#F9F6F0] py-24 lg:py-32 overflow-hidden border-t border-white/5"
    >
      {/* Dark ambient backdrop */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0410] via-[#100620] to-[#0B0410]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#3D1565]/10 blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* TEXT */}
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#E6D5B8]" />
            <span
              className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] font-medium"
              data-testid="about-eyebrow"
            >
              Sobre Nosotros
            </span>
          </div>

          <h2
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.08]"
            data-testid="about-headline"
          >
            Una firma de asesoría diseñada para{" "}
            <span className="italic text-[#E6D5B8]">acompañarte</span> toda la
            vida.
          </h2>

          <div className="mt-8 space-y-5 text-base lg:text-lg text-[#F9F6F0]/75 leading-relaxed max-w-xl font-light">
            <p data-testid="about-paragraph-1">
              EMB Consulting Group fue fundada en{" "}
              <span className="text-[#E6D5B8]">2013</span> en{" "}
              <span className="text-[#E6D5B8]">Playa del Carmen, Quintana Roo</span>{" "}
              por <span className="text-[#E6D5B8]">{FOUNDER.name}</span>,
              Cédula B ante la CNSF. Una década después somos un equipo de{" "}
              <span className="text-[#E6D5B8]">11 asesores</span> con presencia
              nacional.
            </p>
            <p data-testid="about-paragraph-2">
              En <span className="text-[#E6D5B8]">febrero de 2024</span>{" "}
              recibimos oficialmente la <span className="text-[#E6D5B8]">promotoría de Allianz México</span>,
              y mantenemos claves directas con Skandia y Dominion Capital
              Strategies. En seguros, comparamos a las principales aseguradoras
              reguladas del país para elegir contigo la mejor cobertura.
            </p>
            <p data-testid="about-paragraph-3">
              Nuestro fundador es{" "}
              <span className="text-[#E6D5B8]">miembro activo de la Million Dollar Round Table (MDRT)</span>,
              la asociación premier de los mejores asesores financieros del
              mundo — un estándar internacional al que aspira menos del 1% de
              la profesión.
            </p>
          </div>

          {/* Quick stats */}
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg pt-8 border-t border-white/10">
            <div data-testid="about-stat-year">
              <Calendar size={16} className="text-[#E6D5B8] mb-3" />
              <div className="font-display text-3xl text-[#F9F6F0] leading-none">2013</div>
              <div className="text-[10px] uppercase tracking-luxury text-[#A397B4] mt-2">
                Año de fundación
              </div>
            </div>
            <div data-testid="about-stat-team">
              <Award size={16} className="text-[#E6D5B8] mb-3" />
              <div className="font-display text-3xl text-[#F9F6F0] leading-none">11</div>
              <div className="text-[10px] uppercase tracking-luxury text-[#A397B4] mt-2">
                Asesores certificados
              </div>
            </div>
            <div data-testid="about-stat-loc">
              <MapPin size={16} className="text-[#E6D5B8] mb-3" />
              <div className="font-display text-3xl text-[#F9F6F0] leading-none">MX</div>
              <div className="text-[10px] uppercase tracking-luxury text-[#A397B4] mt-2">
                Cobertura nacional
              </div>
            </div>
          </div>
        </div>

        {/* IMAGE */}
        <div className="lg:col-span-6 order-1 lg:order-2 relative">
          <div className="relative">
            <img
              src={IMAGES.aboutUs}
              alt="Marco S. Equis — Fundador EMB Consulting Group"
              className="relative w-full h-[520px] lg:h-[640px] object-cover object-top rounded-sm"
              data-testid="about-image"
            />
            {/* Subtle dark vignette to keep image cohesive with section */}
            <div className="absolute inset-0 pointer-events-none rounded-sm bg-gradient-to-t from-[#0B0410]/70 via-transparent to-[#0B0410]/20" />

            {/* MDRT badge — floating top-right */}
            <div
              data-testid="about-mdrt-badge"
              className="absolute top-5 right-5 lg:top-6 lg:right-6 bg-[#F9F6F0] rounded-sm px-4 py-3 flex items-center gap-3 shadow-2xl border border-[#E6D5B8]"
            >
              <img
                src={BRAND.mdrtLogoColor}
                alt="Member MDRT"
                className="h-12 w-12 object-contain"
              />
              <div className="leading-tight">
                <div className="text-[9px] tracking-luxury uppercase text-[#0B0410]/55">
                  Member
                </div>
                <div className="font-display text-base text-[#0B0410] font-semibold leading-none">
                  MDRT {BRAND.mdrtYear}
                </div>
              </div>
            </div>

            {/* Founder caption */}
            <div className="absolute -bottom-6 -left-6 hidden lg:block bg-[#0B0410] text-[#F9F6F0] px-8 py-6 max-w-xs border border-[#3D1565]">
              <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
                Marco S. Equis
              </div>
              <div className="font-display text-xl leading-tight">
                Fundador · <span className="italic text-[#E6D5B8]">Cédula B · C404868</span>
              </div>
            </div>
            <div className="absolute -top-3 -right-3 w-24 h-24 border-t border-r border-[#E6D5B8]/50 hidden lg:block pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Memberships banner — MDRT + AMASFAC */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 mt-16">
        <div className="text-[11px] tracking-luxury uppercase text-[#A397B4] mb-6 text-center">
          Membresías profesionales
        </div>
        <div
          data-testid="memberships-row"
          className="grid md:grid-cols-3 gap-px bg-white/10 border border-[#E6D5B8]/20 rounded-sm overflow-hidden"
        >
          {/* MDRT */}
          <div
            data-testid="mdrt-membership-card"
            className="relative bg-gradient-to-br from-[#150A1F] to-[#0B0410] p-8 lg:p-10 flex gap-6 items-center overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-48 h-full bg-gradient-to-l from-[#E6D5B8]/5 to-transparent pointer-events-none" />
            <div className="shrink-0 bg-[#F9F6F0] rounded-sm p-3 border border-[#E6D5B8]/40">
              <img
                src={BRAND.mdrtLogoColor}
                alt="Member MDRT 2026"
                data-testid="mdrt-logo"
                className="h-20 w-20 lg:h-24 lg:w-24 object-contain"
              />
            </div>
            <div className="relative">
              <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
                Reconocimiento internacional · {BRAND.mdrtYear}
              </div>
              <h3 className="font-display text-2xl lg:text-3xl font-light text-[#F9F6F0] leading-tight">
                Million Dollar <span className="italic text-[#E6D5B8]">Round Table</span>
              </h3>
              <p className="mt-3 text-xs lg:text-sm text-[#F9F6F0]/65 leading-relaxed font-light">
                Asociación global de élite en seguros y servicios financieros,
                fundada en 1927. Estándar mundial de excelencia profesional.
              </p>
            </div>
          </div>

          {/* AMASFAC */}
          <div
            data-testid="amasfac-membership-card"
            className="relative bg-gradient-to-br from-[#150A1F] to-[#0B0410] p-8 lg:p-10 flex gap-6 items-center overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-48 h-full bg-gradient-to-r from-[#E6D5B8]/5 to-transparent pointer-events-none" />
            <div className="shrink-0 bg-[#0B0410] rounded-sm p-3 border border-[#E6D5B8]/40">
              <img
                src={BRAND.amasfacLogoWhite}
                alt="Miembro AMASFAC"
                data-testid="amasfac-logo"
                className="h-20 w-20 lg:h-24 lg:w-24 object-contain"
              />
            </div>
            <div className="relative">
              <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
                Asociación nacional · México
              </div>
              <h3 className="font-display text-2xl lg:text-3xl font-light text-[#F9F6F0] leading-tight">
                <span className="italic text-[#E6D5B8]">AMASFAC</span>
              </h3>
              <p className="mt-3 text-xs lg:text-sm text-[#F9F6F0]/65 leading-relaxed font-light">
                Asociación Mexicana de Agentes de Seguros y Fianzas — gremio
                profesional que reúne a los agentes más comprometidos con la
                ética y la educación continua en México.
              </p>
            </div>
          </div>

          {/* CANACINTRA */}
          <div
            data-testid="canacintra-membership-card"
            className="relative bg-gradient-to-br from-[#150A1F] to-[#0B0410] p-8 lg:p-10 flex gap-6 items-center overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-48 h-full bg-gradient-to-l from-[#E6D5B8]/5 to-transparent pointer-events-none" />
            <div className="shrink-0 bg-[#F9F6F0] rounded-sm p-3 border border-[#E6D5B8]/40">
              <img
                src={BRAND.canacintraLogo}
                alt="Asociado CANACINTRA"
                data-testid="canacintra-logo"
                className="h-20 w-20 lg:h-24 lg:w-24 object-contain"
              />
            </div>
            <div className="relative">
              <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
                Vicepresidente · Playa del Carmen
              </div>
              <h3 className="font-display text-2xl lg:text-3xl font-light text-[#F9F6F0] leading-tight">
                <span className="italic text-[#E6D5B8]">CANACINTRA</span>
              </h3>
              <p className="mt-3 text-xs lg:text-sm text-[#F9F6F0]/65 leading-relaxed font-light">
                Cámara Nacional de la Industria de Transformación. Marco S.
                Equis preside el área de Desarrollo de Nuevos Negocios y
                Networking en la Delegación Playa del Carmen.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA BANNER — Conviértete en Inversionista Inteligente */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 mt-20 lg:mt-24">
        <a
          href={LIFE_CYCLE_CTA.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="lifecycle-cta-banner"
          className="group block"
        >
          <div
            className="relative overflow-hidden rounded-sm border border-[#3D1565] bg-gradient-to-r from-[#3D1565] via-[#2A0A4A] to-[#3D1565] p-8 lg:p-12 transition-all duration-500 hover:-translate-y-1"
            style={{ boxShadow: "0 30px 60px -30px rgba(61,21,101,0.6)" }}
          >
            <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-[#E6D5B8]/10 to-transparent pointer-events-none" />
            <div className="absolute -top-10 -right-10 w-40 h-40 border border-[#E6D5B8]/20 rounded-full" />
            <div className="absolute -bottom-16 -right-16 w-60 h-60 border border-[#E6D5B8]/10 rounded-full" />

            <div className="relative grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
              <div className="lg:col-span-8">
                <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-3">
                  * Conviértete en inversionista inteligente
                </div>
                <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#F9F6F0] leading-[1.1]">
                  {LIFE_CYCLE_CTA.title.split(" ").slice(0, 2).join(" ")}{" "}
                  <span className="italic text-[#E6D5B8]">
                    {LIFE_CYCLE_CTA.title.split(" ").slice(2).join(" ")}
                  </span>
                </h3>
                <p className="mt-4 text-sm lg:text-base text-[#F9F6F0]/80 leading-relaxed font-light max-w-2xl">
                  {LIFE_CYCLE_CTA.subtitle}
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <div className="inline-flex items-center gap-3 bg-[#F9F6F0] text-[#0B0410] px-7 py-4 rounded-sm group-hover:bg-[#E6D5B8] transition-colors duration-300">
                  <span className="font-display text-base lg:text-lg leading-none">
                    quitameloporquemelogasto.com.mx
                  </span>
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </div>
              </div>
            </div>
          </div>
        </a>
      </div>

      {/* REGULATORS strip — real logos */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 mt-20 lg:mt-24">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-4">
            Marco regulatorio
          </div>
          <p
            data-testid="regulators-intro"
            className="font-display text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.2] text-[#F9F6F0]"
          >
            Todas las instituciones con las que operamos están{" "}
            <span className="italic text-[#E6D5B8]">reguladas por los entes</span>{" "}
            del sistema financiero mexicano.
          </p>
        </div>
        <div
          data-testid="regulators-row"
          className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#0B0410]/15 border border-[#0B0410]/10 rounded-sm overflow-hidden"
        >
          {REGULATORS.map((r) => (
            <div
              key={r.name}
              data-testid={`regulator-${r.name.toLowerCase()}`}
              className="bg-white p-8 lg:p-10 flex flex-col items-center text-center transition-colors duration-500"
            >
              <div className="bg-white rounded-sm p-4 mb-5 h-28 w-full flex items-center justify-center">
                <img
                  src={r.logo}
                  alt={r.name}
                  data-testid={`regulator-logo-${r.name.toLowerCase()}`}
                  className="max-h-full max-w-[180px] object-contain"
                  style={r.invertOnLight ? { filter: "invert(1)" } : undefined}
                />
              </div>
              <div className="font-display text-xl text-[#525252] mb-1">
                {r.name}
              </div>
              <div className="text-[10px] tracking-luxury uppercase text-[#8a8a8a] leading-relaxed max-w-xs">
                {r.full}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
